import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { X } from "lucide-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Icons } from "./ui/icons";
import SearchInput from "./search-input";
import DataTable, { TableRowData } from "./data-table";
import { ppeUserProvisionsTableColumns } from "../lib/table-columns";
import { getPpeUserProvisions, PpeProvision, PpeUser } from "../lib/api";
import { getBackendErrorMessage } from "../lib/utils";
import PpeMaterialRequestNoteDrawer from "./ppe-material-request-note-drawer";
import SidePanel, { PanelHeader } from "./side-panel";
import { useIsMobile } from "../hooks/use-is-mobile";

interface PpeUserProvisionsDrawerProps {
  selectedUser: PpeUser | null;
  onClose: () => void;
}

const formatQuantity = (value: number) =>
  Number.isInteger(value)
    ? String(value)
    : value.toLocaleString(undefined, { maximumFractionDigits: 4 });

const PpeUserProvisionsDrawer: React.FC<PpeUserProvisionsDrawerProps> = ({
  selectedUser,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [materialRequestNoteProvision, setMaterialRequestNoteProvision] =
    useState<PpeProvision | null>(null);

  useEffect(() => {
    if (!selectedUser) return;
    setSearchInput("");
    setSearchTerm("");
    setPage(1);
    setPageSize(10);
  }, [selectedUser?.id]);

  useEffect(() => {
    const handler = setTimeout(() => setSearchTerm(searchInput), 350);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const provisionsQuery = useQuery({
    queryKey: [
      "ppe",
      "users",
      selectedUser?.id,
      "provisions",
      { page, limit: pageSize, search: searchTerm },
    ],
    queryFn: async () =>
      getPpeUserProvisions(selectedUser!.id, page, pageSize, searchTerm),
    enabled: !!selectedUser?.id,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (!provisionsQuery.error) return;
    toast.error(
      getBackendErrorMessage(
        provisionsQuery.error,
        "Failed to fetch user provisions",
      ),
    );
  }, [provisionsQuery.error]);

  const rows = (provisionsQuery.data?.data ?? []).map((item: PpeProvision) => ({
    id: item.id,
    provided_date: item.providedDate
      ? format(new Date(item.providedDate), "MMM dd, yyyy HH:mm")
      : "—",
    ppe_name: item.ppe
      ? `${item.ppe.name}${item.ppe.size ? ` (${item.ppe.size})` : ""}`
      : "—",
    quantity: formatQuantity(Number(item.quantity)),
    unit: item.ppe?.unit || "—",
    provided_by: item.providedByUser?.fullName || "—",
    raw_data: item,
  })) as TableRowData[];

  const provisionActions = [
    {
      label: "Download Material Request Note",
      icon: Icons.Download,
      onClick: (row: TableRowData) => {
        setMaterialRequestNoteProvision(row.raw_data as PpeProvision);
      },
    },
  ];

  return (
    <>
      <SidePanel open={!!selectedUser} onClose={onClose} width="w-[800px]">
        {selectedUser && (
          <div className="relative h-full flex flex-col">
            {isMobile ? (
              <PanelHeader
                title={selectedUser.fullName}
                subtitle={[selectedUser.jobTitle, selectedUser.location?.trim()]
                  .filter(Boolean)
                  .join(" · ")}
                onClose={onClose}
              />
            ) : (
              <div className="px-6 py-4 flex-shrink-0 border-b border-gray-100">
                <div className="flex items-start gap-3">
                  <div className="mr-auto min-w-0">
                    <h2 className="text-lg font-semibold text-foreground font-jakarta">
                      {selectedUser.fullName}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {selectedUser.jobTitle}
                    </p>
                    {selectedUser.location?.trim() && (
                      <p className="text-sm text-gray-500 mt-0.5">
                        {selectedUser.location}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    className="h-8 w-8 p-0 shrink-0"
                    onClick={onClose}
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            <div className="flex-grow overflow-hidden flex flex-col">
              <div
                className={
                  isMobile
                    ? "px-4 pt-3 pb-2 flex-shrink-0"
                    : "px-6 pt-4 pb-2 flex-shrink-0"
                }
              >
                <SearchInput
                  searchTerm={searchInput}
                  onSearchChange={setSearchInput}
                  placeholder="Search provisions..."
                />
              </div>
              <ScrollArea
                className={isMobile ? "flex-1 px-4 pb-4" : "flex-1 px-6 pb-6"}
              >
                <DataTable
                  columns={ppeUserProvisionsTableColumns}
                  data={rows}
                  actions={provisionActions}
                  loading={
                    provisionsQuery.isLoading || provisionsQuery.isFetching
                  }
                  pagination={{
                    page,
                    pageSize,
                    totalItems: provisionsQuery.data?.meta.total ?? 0,
                    totalPages: provisionsQuery.data?.meta.totalPages ?? 1,
                    onPageChange: setPage,
                    onPageSizeChange: (size) => {
                      setPageSize(size);
                      setPage(1);
                    },
                  }}
                />
              </ScrollArea>
            </div>
          </div>
        )}
      </SidePanel>

      <PpeMaterialRequestNoteDrawer
        anchorProvision={materialRequestNoteProvision}
        onClose={() => setMaterialRequestNoteProvision(null)}
      />
    </>
  );
};

export default PpeUserProvisionsDrawer;
